import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Close, Plus } from 'assets/icons';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import StyledInput from 'components/styledInput/StyledInput';
import TextSelectMultipleBigAccount from 'components/textSelectMultiple/textSelectMultipleBigAccount';
import { normalizePennylaneCategoryId } from 'helper/companyOverviewDiff';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';

const ModalCompanyActivity = () => {
  const { toggle } = useModal();
  const {
    modalData,
    setModalDataReturn,
    objID,
    refreshCompanyBigAccount,
    setRefreshCompanyBigAccount,
  } = UseContext();
  const companyInfo = modalData?.companyInfo;
  const [categoryList, setCategoryList] = useState([]);
  const [categoryDraft, setCategoryDraft] = useState(() =>
    normalizePennylaneCategoryId(companyInfo?.pennylane_category_id),
  );
  const [bigAccount, setBigAccount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem('xsrfToken');

  const loadCategories = () =>
    ApiDatabase.getPennyLaneCategories(
      { token },
      (data) => setCategoryList(data?.items || []),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    ApiDatabase.getCompanyBigAccount(
      { token, idCompany: objID },
      (data) => setBigAccount(data?.bigAccount ?? ''),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );
  }, [refreshCompanyBigAccount, objID]);

  const deleteBigAccount = () =>
    ApiDatabase.deleteCompanyBigAccount(
      { token, idCompany: objID },
      () => {
        toast.success('Le grand compte a bien été supprimé');
        setBigAccount('');
        setRefreshCompanyBigAccount((previous) => !previous);
      },
      (error) =>
        toast.error(
          `Une erreur est survenue lors de la suppression du grand compte ${error.response.data.message}.`,
        ),
    );

  const currentCategory = normalizePennylaneCategoryId(
    companyInfo?.pennylane_category_id,
  );
  const hasChanges =
    normalizePennylaneCategoryId(categoryDraft) !== currentCategory;

  const submit = () => {
    if (!hasChanges) return;
    const normalized = normalizePennylaneCategoryId(categoryDraft);
    setIsLoading(true);
    ApiDatabase.postUpdateCompanyOverview(
      {
        token,
        idCompany: companyInfo?._id,
        companyInfo: { pennylane_category_id: normalized },
      },
      () => {
        setIsLoading(false);
        toast.success('Catégorie mise à jour avec succès.');
        setModalDataReturn({
          type: 'company_activity_updated',
          companyInfo: { pennylane_category_id: normalized },
        });
        toggle();
      },
      (err) => {
        setIsLoading(false);
        console.error(err);
        toast.error(
          'Une erreur est survenue lors de la mise à jour du secteur.',
        );
      },
    );
  };

  const selectedDraftCategory = categoryList.find(
    (category) =>
      normalizePennylaneCategoryId(category.id) ===
      normalizePennylaneCategoryId(categoryDraft),
  );

  const categoryOptions = [
    {
      value: null,
      label: 'Aucune catégorie',
      checked: normalizePennylaneCategoryId(categoryDraft) === null,
    },
    ...categoryList.map((category) => ({
      value: category.id,
      label: category.label,
      checked:
        normalizePennylaneCategoryId(category.id) ===
        normalizePennylaneCategoryId(categoryDraft),
    })),
  ];

  const createCategoryFooter = (
    <div
      className="flex items-center gap-1 p-1 cursor-pointer"
      onClick={() => toggle('company_pennylane_create_category')}
    >
      <Plus wh={20} color="#374151" />
      Créer une catégorie
    </div>
  );

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        Activité de l'entreprise
      </Modalheader>
      <ModalBody>
        <div className={'flex flex-col gap-4'}>
          <div className={'flex flex-col gap-1'}>
            Catégorie
            <StyledInput
              type={'selectMultipleRadio'}
              isSearchable
              value={
                normalizePennylaneCategoryId(categoryDraft) === null
                  ? 'Aucune catégorie'
                  : selectedDraftCategory?.label || '-'
              }
              list={categoryOptions}
              dropdownFullWidth
              onChange={(updated) => {
                const selected = updated.find((option) => option.checked);
                setCategoryDraft(
                  normalizePennylaneCategoryId(selected?.value ?? null),
                );
              }}
              footer={createCategoryFooter}
            />
          </div>
          {/* This selector saves on its own, so it sits outside the draft the
              footer confirms — hence the note below it. */}
          <div className={'flex flex-col gap-1'}>
            {bigAccount ? (
              <>
                <div className={'ft-sm text-gray-500'}>Grand compte</div>
                <div
                  className={
                    'flex justify-between items-center gap-2 p-3 border border-gray-200 rounded-lg bg-gray-50'
                  }
                >
                  <div className={'text-gray-900 text-sm font-medium'}>
                    {bigAccount?.name}
                  </div>
                  <button
                    type={'button'}
                    aria-label={'Retirer le grand compte'}
                    className={
                      'border border-gray-300 rounded-md w-8 h-8 flex justify-center items-center hover:bg-gray-50'
                    }
                    onClick={deleteBigAccount}
                  >
                    <Close wh={18} color={'#374151'} />
                  </button>
                </div>
              </>
            ) : (
              // The selector carries its own "Grand compte" label.
              <TextSelectMultipleBigAccount />
            )}
            <div className={'text-gray-500 text-xs'}>
              Le grand compte est enregistré dès sa sélection.
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button size={'LG'} color={'SECONDARY'} onClick={toggle}>
          Annuler
        </Button>
        {isLoading ? (
          <Button size={'LG'} color={'DISABLED'}>
            Confirmation ...
          </Button>
        ) : (
          <Button
            size={'LG'}
            color={hasChanges ? 'PRIMARY' : 'DISABLED'}
            onClick={submit}
          >
            Confirmer
          </Button>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalCompanyActivity;
